import { utagLinkChat } from 'features/tealium';
import { openMessenger } from 'features/genesysCloud';
import { setGenericEvent } from 'features/tealium/ga4/custom';

import { cart } from '../config';
import CartBaseLayoutView from '../../baseViews/LayoutView';
import ButtonValidationView from '../shared/ButtonValidationView';
import MinimumOrderError from '../shared/MinimumOrderError';
import OrderSummaryView from '../shared/OrderSummaryView';
import ProductItemsView from '../shared/ProductItemsView';
import PayPalLaterView from '../shared/PayPalLaterView';
import ShippingOptionsChangedMessage from '../shared/ShippingOptionsChangedMessage';
import InvalidResaleLicenseWarningMessageView from '../shared/InvalidResaleLicenseWarningMessageView';
import OverviewLayoutTemplate from '../templates/OverviewLayoutTemplate';
import dynamicImport from '../../../../features/webpack/dynamicImport.macro';
import SequenceQueue from '../shared/SequenceQueue';

import AddPromoCode from './AddPromoCode';
import ViewMoodBoard from './ViewMoodBoard';
import AddRecommendedBulbs from './AddRecommendedBulbs';
import CartTotal from './CartTotal';
import CheckoutNowButton from './CheckoutNowButton';
import EmailCartLink from './EmailCartLink';
import OverviewHeaderView from './OverviewHeaderView';
import OverviewWarningMessagesView from './OverviewWarningMessagesView';
import PayPalButton from './PayPalButton';
import PriceChangeNotificationView from './PriceChangeNotificationView';
import PrintCartLink from './PrintCartLink';
import PromoCodeStatusMessage from './PromoCodeStatusMessage';
import PromoCodeTermsAndConditionsLink from './PromoCodeTermsAndConditionsLink';
import RemovePromoCodeLink from './RemovePromoCodeLink';
import SelectedPromoCodeMessage from './SelectedPromoCodeMessage';
import OrderTaxExempt from './OrderTaxExempt';

const sequenceQueue = SequenceQueue();

const trackChatEvents = utagLinkChat();

const OverviewLayout = CartBaseLayoutView.extend({
	JSXTemplate: OverviewLayoutTemplate,
	regions: {
		cartTotalTop: '#regionCartTotalTop',
		cartOptions: '#regionCartOptions',
		customerInfo: '#customerInfo',
		internationalMinimumOrderError: '#regionInternationalMinimumOrderError',
		resaleLicenseWarningMessage: '#regionResaleLicenseWarningMessage',
		overviewWarningMessages: '#cartOverviewWarningMessagesContainer',
		productItems: '#productItems',
		addRecommendedBulbs: '#regionAddRecommendedBulbs',
		selectedPromoCodeMessage: '#regionSelectedPromoCodeMessage',
		promoCodeStatusMessage: '#regionPromoCodeStatusMessage',
		promoCodeTermsAndConditionsLink: '#regionPromoCodeTermsAndConditionsLink',
		addPromoCode: '#regionAddPromoCode',
		emailCartLink: '#regionEmailCartLink',
		printCartLink: '#regionPrintCartLink',
		helpLink: '#regionHelpLink',
		viewMoodBoard: '#regionViewMoodBoard',
		removePromoCode: '#regionRemovePromoCode',
		paypalButtonBottom: '#regionPaypalButtonBottom',
		checkoutNowButtonBottom: '#regionCheckoutNowButtonBottom',
		buttonValidationTooltipBottom: '#regionButtonValidationTooltipBottom',
		buttonValidationTooltipBottomPaypal: '#regionButtonValidationTooltipBottomPaypal',
		shippingOptionsChanged: '#regionShippingOptionsChanged',
		priceChangeNotifications: '#priceChangedNotificationModalContent',
		payPalLaterWidget: '#regionPayPalLaterWidget',
		orderSummary: '#orderSummary',
		orderTaxExemptCheckbox: '#regionOrderTaxExemptCheckbox'
	},
	ui: {
		cartChat1aContainer: '#cartChat1aContainer',
		skuOnTheFlyLink: '.skuOnFlyContainer button',
		continueShopping: '.continueShopping'
	},
	events: {
		'click @ui.skuOnTheFlyLink': 'showSkuOnTheFlyModal',
		'click @ui.continueShopping': 'continueShoppingClick'
	},
	initialize () {

		// Grab event bound Chat button to place within the layout
		this.cartChat1aProxy = $('#cartChat1aProxy').detach().removeClass('hidden');

		this.listenTo(this.model, 'change:ProductItems', () => {

			if (this.shouldHideViewMoodBoard()) {

				this.viewMoodBoard.$el.addClass('hidden');

			} else {

				this.viewMoodBoard.$el.removeClass('hidden');

			}

		});

		const { GlobalCartResponseModel } = window.lp.cart?.initialJSON || {};
		sequenceQueue.create(GlobalCartResponseModel?.ProductItems);
		window.lp.sequenceQueue = sequenceQueue;

	},
	shouldHideViewMoodBoard () {
		const hideMoodBoard = window.utag_data.is_kiosk !== '1';
		return this.model.get('ProductItems')?.filter((p) => p.CanUseMoodBoard).length === 0 || hideMoodBoard;

	},
	templateHelpers () {

		const { model } = this;
		const { globals, pageData } = window.lp;
		const { consultantPhoneNumber, isCustomerService, isKiosk } = globals;
		const showCallAndChat = !isCustomerService && !isKiosk;

		const helpers = {
			consultantPhoneNumber,
			continueShoppingUrl: cart.continueShoppingUrl,
			isCustomerService,
			showCallAndChat,
			showChat: showCallAndChat && pageData.isChatToggleOn,
			showSkuOnTheFly: cart.showSkuOnTheFly,
			hideViewMoodBoard: this.shouldHideViewMoodBoard()
		};

		return helpers;

	},
	onRender () {

		const { model } = this;

		// paypal
		// checkout
		// buttonvalidation

		if (window.lp.globals.isCustomerService) {

			this.showChildView('cartOptions', new OverviewHeaderView());

			dynamicImport('desktop/cart/overview-CustomerInfoView', './CustomerInfoView').then((module) => {

				const CustomerInfoView = module.default;
				this.showChildView(
					'customerInfo',
					new CustomerInfoView({
						validateFields: false
					})
				);

			});

		}
		/*
			TODO: Why did rendering MinimumOrderError on Desktop
			but not Mobile previously condition with if (!cartJson.IsGiftCardItemExists) ?
		*/
		this.showChildView('cartTotalTop', new CartTotal({ model }));
		this.showChildView('internationalMinimumOrderError', new MinimumOrderError({ model }));
		this.showChildView('resaleLicenseWarningMessage', new InvalidResaleLicenseWarningMessageView({ model }));
		this.showChildView('overviewWarningMessages', new OverviewWarningMessagesView({ model }));
		this.showChildView('productItems', new ProductItemsView({ model, showProductRating: true }));
		this.showChildView('addRecommendedBulbs', new AddRecommendedBulbs({ model }));

		if (window.lp.globals.isCustomerService) {

			dynamicImport('desktop/cart/overview-AddSkuView', './AddSkuView').then((module) => {

				const AddSkuView = module.default;
				// Empty Cart Add Sku
				const addSkuView = new AddSkuView({
					el: '#addSkuContainer'
				});
				addSkuView.render();

			});

		}

		// Order Summary View
		const orderSummaryView = new OrderSummaryView({ model });
		this.showChildView('orderSummary', orderSummaryView);
		this.showChildView(
			'payPalLaterWidget',
			new PayPalLaterView({
				model,
				placement: 'cart',
				trackingEventCategory: 'Cart',
				// If PayPal Later is disabled on page load, it may be enabled later (eg. change country) without reload
				fetchPayPalJsIfMissing: true
			})
		);
		this.showChildView('checkoutNowButtonBottom', new CheckoutNowButton({ model }));
		this.showChildView(
			'orderTaxExemptCheckbox',
			new OrderTaxExempt({
				model
			})
		);
		this.showChildView(
			'buttonValidationTooltipBottom',
			new ButtonValidationView({
				model,
				aboveOrBelow: 'above',
				tooltip: '.cartCalloutButtons .buttonValidationTooltipContainer'
			})
		);
		this.showChildView(
			'paypalButtonBottom',
			new PayPalButton({
				model,
				paypalButtonId: 'paypalBottom',
				allowAdditionalPayPalPaymentMethods: true
			})
		);
		this.showChildView(
			'buttonValidationTooltipBottomPaypal',
			new ButtonValidationView({
				model,
				aboveOrBelow: 'above',
				tooltip: '.cartCalloutButtons .buttonValidationTooltipContainerPaypal'
			})
		);

		if (this.templateHelpers().showCallAndChat && this.cartChat1aProxy.length > 0) {

			this.ui.cartChat1aContainer.empty().append(this.cartChat1aProxy);

		}

		this.showChildView('selectedPromoCodeMessage', new SelectedPromoCodeMessage({ model }));
		this.showChildView('promoCodeStatusMessage', new PromoCodeStatusMessage({ model }));
		this.showChildView('promoCodeTermsAndConditionsLink', new PromoCodeTermsAndConditionsLink({ model }));
		this.showChildView('removePromoCode', new RemovePromoCodeLink({ model }));
		this.showChildView('addPromoCode', new AddPromoCode({ model }));
		this.showChildView('viewMoodBoard', new ViewMoodBoard({ model }));
		this.showChildView('printCartLink', new PrintCartLink());
		this.showChildView('emailCartLink', new EmailCartLink());
		this.showChildView('shippingOptionsChanged', new ShippingOptionsChangedMessage());
		this.showChildView('priceChangeNotifications', new PriceChangeNotificationView({ model }));

		const liveChat = document.querySelector('.chatWrapperWithIcon button');

		if (liveChat) {

			trackChatEvents({
				eventAction: 'Load-Chat',
				eventLabel: 'Cart',
				nonInteraction: 1
			});

		}

	},
	showSkuOnTheFlyModal (e) {

		e.preventDefault();

		const $this = $(e.target);

		$this.lpModal({
			onInit: true,
			src: $this.attr('data-src'),
			width: 600,
			height: 410
		});

	},
	continueShoppingClick () {

		setGenericEvent({ event_name: 'cart_shop_click' });

	}
});

export default OverviewLayout;
